import { useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import type { TodoListResponse } from '@/lib/api';

interface ListSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  list: TodoListResponse;
  onSave: (input: {
    isPublic?: boolean;
    publicSlug?: string;
    reviewMode?: boolean;
  }) => Promise<void> | void;
  onDelete: () => Promise<void> | void;
}

export function ListSettingsDialog({
  open,
  onClose,
  list,
  onSave,
  onDelete,
}: ListSettingsDialogProps) {
  const [isPublic, setIsPublic] = useState(list.isPublic);
  const [publicSlug, setPublicSlug] = useState(list.publicSlug ?? '');
  const [reviewMode, setReviewMode] = useState(list.reviewMode);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setIsPublic(list.isPublic);
      setPublicSlug(list.publicSlug ?? '');
      setReviewMode(list.reviewMode);
      setSaveError(null);
      setDeleteError(null);
    }
  }, [open, list]);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      onClose();
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      await onSave({
        isPublic,
        publicSlug: isPublic ? publicSlug.trim() || undefined : undefined,
        reviewMode,
      });
      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleConfirmDelete() {
    setDeleting(true);
    setDeleteError(null);
    try {
      await onDelete();
      setDeleteConfirmOpen(false);
      setDeleteConfirmText('');
      onClose();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>List Settings</DialogTitle>
          <DialogDescription>Configure visibility and sharing for this list.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="review-toggle">Require review before done</Label>
              <p className="text-xs text-muted-foreground">
                Checking an item off sends it to an &ldquo;In Review&rdquo; step. A reviewer then
                verifies it before it counts as completed.
              </p>
            </div>
            <Switch id="review-toggle" checked={reviewMode} onCheckedChange={setReviewMode} />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="public-toggle">Make Public</Label>
              <p className="text-xs text-muted-foreground">
                Allow anyone with the link to view this list
              </p>
            </div>
            <Switch id="public-toggle" checked={isPublic} onCheckedChange={setIsPublic} />
          </div>

          {isPublic && (
            <div className="space-y-2">
              <Label htmlFor="public-slug">Public Slug</Label>
              <Input
                id="public-slug"
                value={publicSlug}
                onChange={(e) => setPublicSlug(e.target.value.toLowerCase())}
                placeholder="my-todo-list"
              />
              {publicSlug.trim() && (
                <p className="text-xs text-muted-foreground">
                  Preview:{' '}
                  <code className="rounded bg-muted px-1 py-0.5">
                    /a/arche_todo_lists/public/{publicSlug.trim()}
                  </code>
                </p>
              )}
            </div>
          )}

          {saveError && <p className="text-sm text-destructive">{saveError}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>

        <div className="mt-6 border-t pt-4 space-y-2">
          <Label className="text-destructive">Danger zone</Label>
          <p className="text-xs text-muted-foreground">
            Deleting this list permanently removes all sections, items, and sharing settings.
          </p>
          <AlertDialog
            open={deleteConfirmOpen}
            onOpenChange={(nextOpen) => {
              setDeleteConfirmOpen(nextOpen);
              if (!nextOpen) {
                setDeleteConfirmText('');
                setDeleteError(null);
              }
            }}
          >
            <AlertDialogTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete list
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this list?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete <span className="font-semibold">{list.name}</span>{' '}
                  and all of its sections and items. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="py-2">
                <p className="text-sm text-muted-foreground mb-2">
                  Type <span className="font-mono font-semibold">delete</span> to confirm:
                </p>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="delete"
                  autoFocus
                />
                {deleteError && <p className="text-sm text-destructive mt-2">{deleteError}</p>}
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={deleting || deleteConfirmText.trim().toLowerCase() !== 'delete'}
                  onClick={(e) => {
                    e.preventDefault();
                    handleConfirmDelete();
                  }}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </DialogContent>
    </Dialog>
  );
}
